import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { AreaChart, BarChart, PieChart, GaugeChart, ChartContainer } from '@/components/charts';
import { useOperationsSummary, useOperationsTrends, useSlaCompliance, useWorkloadAnalysis } from '../hooks';
import {
  RefreshCw, Ticket, CheckCircle, Clock, Flame,
  Timer, PieChartIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SlaComplianceItem, WorkloadItem } from '../types';

export default function OperationsPage() {
  const { t } = useTranslation('intelligence');

  const { data: summary, isLoading: summaryLoading, refetch, isFetching } = useOperationsSummary();
  const { data: trends, isLoading: trendsLoading } = useOperationsTrends();
  const { data: sla, isLoading: slaLoading } = useSlaCompliance();
  const { data: workload, isLoading: workloadLoading } = useWorkloadAnalysis();

  const formatHours = (hours: number | undefined) => {
    if (!hours) return '--';
    if (hours < 24) return `${hours.toFixed(1)}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const trendChartData = trends?.map(item => ({
    name: item.date,
    opened: item.opened,
    closed: item.closed,
  })) || [];

  const agingChartData = [
    { name: '< 1 day', value: summary?.aging?.under_1_day || 0 },
    { name: '1-3 days', value: summary?.aging?.days_1_3 || 0 },
    { name: '3-7 days', value: summary?.aging?.days_3_7 || 0 },
    { name: '7-14 days', value: summary?.aging?.days_7_14 || 0 },
    { name: '> 14 days', value: summary?.aging?.over_14_days || 0 },
  ];

  const categoryChartData = summary?.by_category?.map(item => ({
    name: item.category,
    value: item.count,
  })) || [];

  const getComplianceClass = (rate: number) => {
    if (rate >= 90) return 'bg-green-100 text-green-800';
    if (rate >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getPriorityLabel = (priority: string) => {
    const labels: Record<string, string> = {
      p1: t('operations.priority.p1', 'P1 - Critical'),
      p2: t('operations.priority.p2', 'P2 - High'),
      p3: t('operations.priority.p3', 'P3 - Medium'),
      p4: t('operations.priority.p4', 'P4 - Low'),
    };
    return labels[priority] || priority;
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('operations.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('operations.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <MetricCard
          title={t('operations.cards.openTickets')}
          value={summary?.open_tickets}
          icon={<Ticket className="h-5 w-5" />}
          color="blue"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('operations.cards.closedToday')}
          value={summary?.closed_today}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('operations.cards.overdueTickets')}
          value={summary?.overdue_tickets}
          icon={<Clock className="h-5 w-5" />}
          color="red"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('operations.cards.highPriorityOpen')}
          value={summary?.high_priority_open}
          icon={<Flame className="h-5 w-5" />}
          color="orange"
          loading={summaryLoading}
        />
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Timer className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('operations.metrics.avgResolution')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-gray-900">
                    {formatHours(summary?.avg_resolution_hours)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <PieChartIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('operations.metrics.slaMetRate')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-gray-900">
                    {summary?.sla_met_rate?.toFixed(1)}%
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends + SLA Gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('operations.sections.ticketTrends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={trendsLoading} height={280}>
              <AreaChart
                data={trendChartData}
                series={[
                  { dataKey: 'opened', name: t('operations.charts.opened'), color: '#3498db' },
                  { dataKey: 'closed', name: t('operations.charts.closed'), color: '#27ae60' },
                ]}
                height={250}
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('operations.sections.slaCompliance')}</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : (
              <GaugeChart
                value={summary?.sla_met_rate || 0}
                max={100}
                height={200}
                label={t('operations.metrics.slaMetRate')}
                suffix="%"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Aging + Category Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('operations.sections.ticketAging')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={summaryLoading} height={250}>
              <BarChart
                data={agingChartData}
                height={220}
                colors={['#27ae60', '#3498db', '#f39c12', '#e67e22', '#e74c3c']}
                colorByValue
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('operations.sections.byCategory')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={summaryLoading} height={250}>
              <PieChart
                data={categoryChartData}
                height={220}
                showLegend
                innerRadius={50}
              />
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* SLA Compliance Table + Workload Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('operations.sections.slaByPriority')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.priority')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.targetHours')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.actualHours')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.compliance')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.breaches')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {slaLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : sla && (sla as SlaComplianceItem[]).length > 0 ? (
                    (sla as SlaComplianceItem[]).map((item: SlaComplianceItem, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {getPriorityLabel(item.priority)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {item.target_hours}h
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {item.actual_hours?.toFixed(1)}h
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={cn('text-xs', getComplianceClass(item.compliance_rate || 0))}>
                            {item.compliance_rate?.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={cn(
                            'font-medium',
                            (item.breach_count || 0) > 0 && 'text-red-600'
                          )}>
                            {item.breach_count || 0}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        {t('operations.noSlaData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('operations.sections.workloadDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.assignee')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.open')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.closed')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('operations.table.total')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {workloadLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : workload && (workload as WorkloadItem[]).length > 0 ? (
                    (workload as WorkloadItem[]).map((item: WorkloadItem, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {item.assignee_name || t('operations.unassigned')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="secondary">{item.open_count}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className="bg-green-100 text-green-800">{item.closed_count}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          {item.total_count}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        {t('operations.noWorkloadData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
